import { Button, Notification, Table, Tag, Title } from 'rbx';
import React, { useState } from 'react';
import { format } from 'date-fns';

const date = value =>
  value ? format(new Date(value), 'do MMM yyyy hh:mm a') : '-';

function statusColor(status) {
  switch (status) {
    case 'success':
      return 'success';
    case 'pending':
    case 'pending_refund':
      return 'warning';
    case 'failed':
    case 'refunded':
    case 'charged_back':
      return 'danger';
    default:
      return 'light';
  }
}

/**
 * The charges behind an add-on subscription, with a refund action.
 *
 * A refund ends the subscription it paid for — the same as a refund made in
 * the PSP dashboard — so it asks for confirmation first.
 *
 * @param {boolean} showDomain - Add the domain column (.cv orders).
 * @param {boolean} showCredits - Add the credits column (auto-apply orders).
 */
function OrdersTable({ orders = [], onRefund, showDomain, showCredits }) {
  const [refunding, setRefunding] = useState(null);
  const [message, setMessage] = useState(null);

  const refund = order => async () => {
    const ok = window.confirm(
      `Refund ${order.amount} ${
        order.currency
      }? This also ends the subscription${
        showDomain
          ? ` and releases ${order.olaDomain}`
          : ' and removes its credits'
      }.`,
    );
    if (!ok) return;
    setRefunding(order.orderId);
    setMessage(null);
    try {
      await onRefund(order.orderId);
      setMessage({ color: 'success', text: 'Refunded.' });
    } catch (e) {
      setMessage({ color: 'danger', text: e.message });
    } finally {
      setRefunding(null);
    }
  };

  return (
    <>
      <Title subtitle size={5}>
        Orders
      </Title>
      {message && (
        <Notification color={message.color}>{message.text}</Notification>
      )}
      {!orders.length ? (
        <p>No orders</p>
      ) : (
        <Table fullwidth striped hoverable>
          <Table.Head>
            <Table.Row>
              <th>Order ID</th>
              {showDomain && <th>Domain</th>}
              <th>Amount</th>
              <th>Currency</th>
              {showCredits && <th>Credits</th>}
              <th>Status</th>
              <th>Card</th>
              <th>Date</th>
              <th>Action</th>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {orders.map(order => (
              <Table.Row key={order.orderId}>
                <Table.Cell>{order.orderId}</Table.Cell>
                {showDomain && (
                  <Table.Cell>{order.olaDomain || '-'}</Table.Cell>
                )}
                <Table.Cell>{order.amount}</Table.Cell>
                <Table.Cell>{order.currency}</Table.Cell>
                {showCredits && (
                  <Table.Cell>{order.creditsGranted || '-'}</Table.Cell>
                )}
                <Table.Cell>
                  <Tag color={statusColor(order.status)}>{order.status}</Tag>
                </Table.Cell>
                <Table.Cell>
                  {order.cardLastFour
                    ? `${order.cardBrand || ''} •••• ${order.cardLastFour}`
                    : '-'}
                </Table.Cell>
                <Table.Cell>{date(order.createdAt)}</Table.Cell>
                <Table.Cell>
                  <Button
                    size="small"
                    color="danger"
                    onClick={refund(order)}
                    disabled={order.status !== 'success' || !!refunding}
                  >
                    {refunding === order.orderId ? 'Refunding' : 'Refund'}
                  </Button>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </>
  );
}

export default OrdersTable;
